/**
 * Global fetch gate for the web app — attaches the auth token to every
 * same-origin /api/ request, and handles token expiry in one place.
 *
 * WHY THIS EXISTS
 * ---------------
 * Auth on the frontend was opt-in: each caller had to remember
 * `headers: authHeaders(token)`. On 2026-09-17, 18 of 33 files that call
 * /api/ did not — Home, Invoices, Customers, Uploads, Cash, Claims,
 * Disputes, Carriers and the rest all used a bare `fetch('/api/...')`.
 *
 * That did not show up as a bug, because at the time the API did not check.
 * The same audit found 103 of 145 endpoints had no authentication at all.
 * Deploying the server-side gate (api/src/lib/require-auth-hook.ts) without
 * this file would 401 most of the dashboard — an outage traded for an
 * exposure.
 *
 * THE FIX IS THE DEFAULT, NOT THE CALL SITES
 * ------------------------------------------
 * Editing 60-odd call sites fixes today and guarantees a repeat: the next
 * `fetch('/api/...')` anyone writes forgets again. That is precisely how the
 * server side ended up with 103 open endpoints. So the token is attached by
 * default and a call site has to do nothing to be correct.
 *
 * This is the frontend mirror of require-auth-hook.ts. Read them together.
 *
 * WHAT THIS IS NOT
 * ----------------
 * This proves WHO you are. It does not decide what you may do — that is
 * requireRole()/permissions on the server. A logged-in member can still reach
 * admin-level endpoints until those get per-route role checks. Tracked
 * separately; do not read this file as closing that gap.
 *
 * NOTE (2026-09-17): this is a bridge for the current platform. The planned
 * rebuild should carry the token in a typed API client rather than by
 * patching global fetch — see the TODO task.
 */

import { TOKEN_KEY, tokenIfValid } from './auth'

// ── what gets a token ─────────────────────────────────────────────────────────

/**
 * Resolve whatever fetch was handed into a URL we can reason about.
 * Returns null when it cannot be resolved — in which case we pass it through
 * untouched rather than guess.
 */
function resolveUrl(input: RequestInfo | URL): URL | null {
  try {
    // Base is location.href, NOT location.origin: fetch resolves a relative
    // URL against document.baseURI, so `fetch('api/x')` from /customers/y
    // really goes to /customers/api/x. Resolving against the origin would
    // compute /api/x and attach a token to a request that never hits our API.
    if (typeof input === 'string')   return new URL(input, window.location.href)
    if (input instanceof URL)        return input
    // Request.url is already absolute and fully resolved.
    if (input instanceof Request)    return new URL(input.url)
    return null
  } catch {
    return null
  }
}

/**
 * Only same-origin /api/ paths get the Authorization header.
 *
 * The origin check is the important half. Attaching a bearer token to a
 * third-party host would leak a credential that grants full access to the
 * financial data — a worse bug than the one this file fixes. Absolute URLs to
 * anywhere else pass through with nothing added.
 */
function isOurApi(url: URL): boolean {
  return url.origin === window.location.origin && url.pathname.startsWith('/api/')
}

// One unstated dependency, recorded so nobody has to rediscover it: if our own
// API ever 3xx'd to another origin, the token would follow the redirect. It
// does not, because the Fetch spec requires browsers to strip Authorization on
// a cross-origin redirect. That is a guarantee we are leaning on rather than
// enforcing. If /api/ ever starts redirecting off-origin, add
// `redirect: 'manual'` here instead of trusting it.

/**
 * Endpoints where a 401 is a normal answer rather than an expired session:
 *
 *   /api/auth/login   — a wrong password returns 401. Clearing state and
 *                       redirecting here would wipe the form and loop.
 *   /api/auth/logout  — already logging out; a 401 changes nothing.
 */
function is401Expected(url: URL): boolean {
  return url.pathname === '/api/auth/login' || url.pathname === '/api/auth/logout'
}

// ── token ─────────────────────────────────────────────────────────────────────

/**
 * The token comes from auth.tsx's tokenIfValid(), which reads the same
 * localStorage key the app boots from. Deliberately NOT re-implemented here:
 * a second copy of the expiry check would drift from decodeToken() and the
 * two would disagree about when a session ended.
 *
 * localStorage rather than React state because this runs outside React. An
 * expired token is treated as absent — sending it just produces a 401 with
 * extra steps, and this keeps the "no token" and "stale token" paths
 * identical.
 */

// ── expired-session handling ──────────────────────────────────────────────────

// A page load fires many requests at once. Without this, an expired token
// would trigger one redirect per in-flight request.
let redirecting = false

function sessionExpired(): void {
  if (redirecting) return
  redirecting = true

  try { localStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }

  // Already on the login screen — nothing to redirect to.
  if (window.location.pathname === '/login') { redirecting = false; return }

  // replace(), not assign(): the token has just been deleted, so a Back
  // navigation would land on a page that immediately 401s and bounce here
  // again. There is nothing useful to go back to.
  //
  // A full load rather than a router push because this runs outside React.
  try {
    window.location.replace('/login')
  } catch {
    // A sandboxed frame can refuse navigation. Swallow it — letting this
    // throw would turn a 401 Response into a rejected promise at the call
    // site, which is a stranger failure than the 401 itself.
  }

  // Navigation can also be cancelled by a beforeunload handler, in which case
  // replace() returns without throwing and the page stays put. Releasing the
  // latch means a later 401 can try again; leaving it set would swallow every
  // subsequent 401 with the token already gone, and wedge the app on error
  // states. Harmless if the navigation does succeed — the timer dies with the
  // page.
  setTimeout(() => { redirecting = false }, 3000)
}

// ── install ───────────────────────────────────────────────────────────────────

// Vite HMR re-runs modules. Without this guard each reload would wrap the
// previous wrapper, stacking interceptors until something overflows.
const INSTALLED = Symbol.for('byelabel.apiFetchInstalled')

export function installApiFetch(): void {
  const w = window as unknown as Record<symbol, boolean>
  if (w[INSTALLED]) return
  w[INSTALLED] = true

  const nativeFetch: typeof fetch = window.fetch.bind(window)

  window.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url = resolveUrl(input)

    // Not ours, or unresolvable — hands off entirely.
    if (!url || !isOurApi(url)) return nativeFetch(input, init)

    const token = tokenIfValid()

    // The headers the request will ACTUALLY go out with.
    //
    // This is the subtle part. fetch(request, init) re-runs the Request
    // constructor, and an init.headers REPLACES the Request's whole header
    // list rather than merging into it. So the effective set is init.headers
    // when present, and the Request's own only when it is not — and anything
    // we add has to go into that same construction, never a second argument.
    // Getting this wrong drops the Authorization header silently, which is a
    // 401 with no visible cause.
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    )

    let response: Response

    // Nothing to add — either no valid session, or the caller set its own
    // Authorization. An explicit header always wins, so the 15 files already
    // passing authHeaders(token) behave exactly as before and a caller can
    // still deliberately send a different token.
    //
    // Pass straight through rather than rebuilding, so a Request body is not
    // disturbed and stays reusable.
    if (!token || headers.has('Authorization')) {
      response = await nativeFetch(input, init)
    } else {
      headers.set('Authorization', `Bearer ${token}`)
      response = input instanceof Request
        // Copies method, body, credentials, mode and the rest across.
        ? await nativeFetch(new Request(input, { ...init, headers }))
        : await nativeFetch(input, { ...init, headers })
    }

    // A 401 from our own API means the session is gone. Without this the body
    // `{"error":"..."}` flows into the caller's .json() and renders as garbage
    // — which is what every stale tab would show the moment the server-side
    // auth gate goes live.
    if (response.status === 401 && !is401Expected(url)) sessionExpired()

    return response
  }
}
